// ─────────────────────────────────────────────────────────────────────────────
// router/index.ts
// ─────────────────────────────────────────────────────────────────────────────

import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore, BOOTSTRAP_TIMEOUT_MS } from '@/stores/authStore';
import { useCompanyStore } from '@/stores/companyStore';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ── Public ────────────────────────────────────────────────────────────────
    {
      path:      '/login',
      name:      'login',
      component: () => import('@/views/LoginView.vue'),
      meta:      { requiresAuth: false },
    },

    // ── Die Annahme einer Einladung (T-M30.6, REQ-SEC-030) ────────────────
    //
    // ⚠ DIE DRITTE OEFFENTLICHE ROUTE — neben `/login` und `/auth/action`
    // (REQ-SEC-012, datierter Eintrag 2026-09-01). Die drei Bildschirm-Screens
    // stehen gesondert: sie sind Geraeteadressen mit eigenem Anspruchsweg
    // (`screentoken`, REQ-SEC-004) und zaehlen in dieser Reihe nicht mit.
    //
    // `requiresAuth: false` UND `noCompanyRequired: true`: der Aufrufer ist
    // nicht angemeldet und hat noch kein Konto — es entsteht erst hier
    // (REQ-SEC-030 (b)). **Der Anspruch IST das Geheimnis im Pfad**; geprueft
    // wird es serverseitig in `acceptinvitation` gegen den SHA-256-Hash, nie
    // hier. Der Pfad `/einladung/` ist die Vertragsflaeche zur Einladungsmail
    // (`firebase/functions/src/invitations.ts`, `INVITATION_ACCEPT_PATH`).
    {
      path:      '/einladung/:geheimnis',
      name:      'einladung',
      component: () => import('@/views/EinladungView.vue'),
      meta:      { requiresAuth: false, noCompanyRequired: true },
    },

    // ── Kitchen Display System (TV-Screen, kein Login nötig) ──────────────
    //
    // ZWEI ADRESSEN, EINE ANSICHT (T-M25.1, REQ-SC-103 (1)): der Modus ergibt
    // sich aus dem geöffneten Link und nicht mehr aus einem Kanalfeld. Die
    // Light-Adresse liegt BEWUSST innerhalb von `/kitchen/` — sonst entstünde
    // ein vierter Bildschirm-Geltungsbereich und damit ein zweiter
    // Dienst-Arbeiter-Bereich (REQ-SC-103 (2), REQ-SC-076, REQ-OPS-057 (b)).
    // Die Rechnung Adresse → Modus liegt in `utils/screenModeRoute.ts`.
    {
      path:      '/kitchen/:printerId',
      name:      'kitchen',
      component: () => import('@/views/KitchenView.vue'),
      meta:      { requiresAuth: false },
    },
    {
      path:      '/kitchen/:printerId/light',
      name:      'kitchen-light',
      component: () => import('@/views/KitchenView.vue'),
      meta:      { requiresAuth: false },
    },

    // ── Abholscreen (TV-Screen, kein Login nötig) ───────────────────────
    {
      path:      '/abholscreen/:printerId',
      name:      'abholscreen',
      component: () => import('@/views/AbholscreenView.vue'),
      meta:      { requiresAuth: false },
    },

    // ── Fastline Suchseite (kein Login nötig) ───────────────────────
    {
      path:      '/fastline/:printerId',
      name:      'fastline',
      component: () => import('@/views/FastlineView.vue'),
      meta:      { requiresAuth: false },
    },

    // ── Firma auswählen (nach Login, vor Kontext) ──────────────────────────
    {
      path:      '/select-company',
      name:      'select-company',
      component: () => import('@/views/CompanySelectView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },

    // ── Einstellungen – Firma ──────────────────────────────────────────────
    {
      path:      '/settings/company',
      name:      'settings-company',
      component: () => import('@/views/settings/CompanyView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/settings/company/new',
      name:      'company-new',
      component: () => import('@/views/settings/CompanyDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/settings/company/:id',
      name:      'company-detail',
      component: () => import('@/views/settings/CompanyDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },

    // ── Einstellungen – Benutzer ───────────────────────────────────────────
    //
    // ⚠ DIE DREI ADMIN-FLAECHEN TRAGEN SEIT T-M30.7 `requiresAdmin`
    // (REQ-SEC-027 (c)): Benutzerverwaltung, `settings` (SMTP/Twilio) und
    // API-Tokens. **Die Aufzaehlung ist ABSCHLIESSEND** — jede andere Ansicht
    // bleibt fuer die Rolle `user` sichtbar UND bedienbar, ausdruecklich
    // einschliesslich der Standorte samt `updateChannel` und `pinnedVersion`
    // (Entscheid des Auftraggebers vom 2026-09-01 zu OP-185). *Wer dort ein
    // Gate ergaenzt, baut die Sperre nach, die der Auftraggeber gestrichen hat.*
    {
      path:      '/settings/users',
      name:      'settings-users',
      component: () => import('@/views/settings/UsersView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true, requiresAdmin: true },
    },
    {
      path:      '/settings/users/new',
      name:      'user-new',
      component: () => import('@/views/settings/UserDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true, requiresAdmin: true },
    },
    {
      path:      '/settings/users/:id',
      name:      'user-detail',
      component: () => import('@/views/settings/UserDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true, requiresAdmin: true },
    },
    {
      path:      '/settings/smtp',
      name:      'settings-smtp',
      component: () => import('@/views/settings/SmtpSettingsView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true, requiresAdmin: true },
    },

    // ── Einstellungen – Standorte (REQ-DM-011) ─────────────────────────────
    {
      path:      '/settings/locations',
      name:      'settings-locations',
      component: () => import('@/views/settings/LocationsView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/settings/locations/new',
      name:      'location-new',
      component: () => import('@/views/settings/LocationDetailView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/settings/locations/:id',
      name:      'location-detail',
      component: () => import('@/views/settings/LocationDetailView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/settings/api',
      name:      'settings-api',
      component: () => import('@/views/settings/ApiSettingsView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true, requiresAdmin: true },
    },
    // Firebase Auth action handler (password reset redirect)
    {
      path:      '/auth/action',
      name:      'auth-action',
      component: () => import('@/views/AuthActionView.vue'),
      meta:      { requiresAuth: false },
    },

    // ── Einstellungen – Printserver Settings ──────────────────────────────
    {
      path:      '/settings/printserver',
      name:      'settings-printserver',
      component: () => import('@/views/settings/PrintserverSettingsView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/settings/printserver/new',
      name:      'settings-printserver-new',
      component: () => import('@/views/settings/PrintserverDetailSettingsView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/settings/printserver/:id',
      name:      'settings-printserver-detail',
      component: () => import('@/views/settings/PrintserverDetailSettingsView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:     '/settings',
      redirect: '/settings/company',
    },

    // ── Firmen-Kontext erforderlich ────────────────────────────────────────
    {
      path:     '/',
      redirect: '/jobs',   // Immer auf /jobs weiterleiten
    },
    {
      path:      '/dashboard',
      name:      'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/jobs',
      name:      'jobs',
      component: () => import('@/views/JobsView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/fleet',
      name:      'fleet',
      component: () => import('@/views/FleetView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/printers',
      name:      'printers',
      component: () => import('@/views/PrintersView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/printers/new',
      name:      'printer-new',
      component: () => import('@/views/PrinterDetailView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/printers/:id',
      name:      'printer-detail',
      component: () => import('@/views/PrinterDetailView.vue'),
      meta:      { requiresAuth: true },
    },
    {
      path:      '/schankanlage',
      name:      'schankanlage',
      component: () => import('@/views/SchankanlageView.vue'),
      meta:      { requiresAuth: true },
    },

    // ── Printserver ────────────────────────────────────────────────────────
    {
      path:      '/printserver',
      name:      'printserver',
      component: () => import('@/views/PrintserverView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/printserver/new',
      name:      'printserver-new',
      component: () => import('@/views/PrintserverDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path:      '/printserver/:id',
      name:      'printserver-detail',
      component: () => import('@/views/PrintserverDetailView.vue'),
      meta:      { requiresAuth: true, noCompanyRequired: true },
    },

    // ── Catch-all ─────────────────────────────────────────────────────────
    {
      path:     '/:pathMatch(.*)*',
      redirect: '/jobs',   // Unbekannte Routen → /jobs
    },
  ],
});

// ── Navigation Guard ──────────────────────────────────────────────────────────

/**
 * Wartet auf das Ende des Anmelde-Starts — **nie laenger als die Frist des
 * Starts selbst** (REQ-OPS-052 (e), T-M21.2).
 *
 * ⚠ **BIS ZUM 2026-08-18 WARTETE DIESE STELLE UNBEGRENZT.** Blieb `isLoading`
 * stehen — weil das Lesen des Profils warf oder gar nicht antwortete —, loeste
 * die erste Navigation nie auf, und die Oberflaeche blieb im Ladebild: genau
 * das Feldsymptom (b) vom 2026-08-17/18. Ein Waechter, der laenger wartet als
 * der Start dauern darf, ist eine zweite Stelle, an der die Anwendung haengen
 * bleibt — und zwar eine, die kein Ausgang je erreicht.
 *
 * Gibt `true` zurueck, wenn die Frist abgelaufen ist; der Aufrufer faellt dann
 * in denselben Fehlerfall wie der Store (`failBootstrapTimeout`).
 *
 * Die Frist ist NICHT hier gefuehrt: sie kommt als `BOOTSTRAP_TIMEOUT_MS` aus
 * dem Auth-Store — eine zweite Zahl waere zwei Wahrheiten (REQ-OPS-052 (b)).
 */
export function warteAufAnmeldestart(
  store: { isLoading: boolean; $subscribe: (cb: () => void) => () => void },
  fristMs: number = BOOTSTRAP_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!store.isLoading) { resolve(false); return; }   // ← already resolved
    let fertig = false;
    const zeitgeber = setTimeout(() => {
      if (fertig) return;
      fertig = true;
      stop();
      resolve(true);            // Frist abgelaufen — der Start gilt als gescheitert
    }, fristMs);
    const beende = (abgelaufen: boolean) => {
      if (fertig) return;
      fertig = true;
      clearTimeout(zeitgeber);
      stop();
      resolve(abgelaufen);
    };
    const stop = store.$subscribe(() => {
      if (!store.isLoading) beende(false);
    });
  });
}

/**
 * Wartet, bis der Anspruch des angemeldeten Benutzers GELESEN ist — **nie
 * laenger als die Frist des Anmeldestarts** (T-M47.2 Kriterium 5, 2026-10-06;
 * REQ-OPS-058 Praez. 2 (e) samt Nachtrag 2026-10-05 an Praez. 4).
 *
 * ⚠ **DIE LUECKE, DIE SIE SCHLIESST (Befund B2, M47 V.3):** bei der FRISCHEN
 * Anmeldung endet der Anmeldestart schon mit `signedOut` — `isLoading` steht
 * auf `false`, wenn der Benutzer eintrifft, und steigt nicht wieder.
 * {@link warteAufAnmeldestart} kehrt deshalb sofort zurueck, und bis zum
 * 2026-10-06 entschied der Waechter in diesem Fenster ueber die gespeicherte
 * Kennung, BEVOR der Anspruch gelesen war: `pruefeMandantenwahl` sprang mangels
 * Anspruch zurueck, und die Entscheidung fiel am Lesevorgang unter den Rules
 * statt am Anspruch. *Beim Neuladen trat das nie auf — dort bleibt `isLoading`
 * bis nach dem gelesenen Anspruch stehen.*
 *
 * Das Signal ist dasselbe wie im `companyStore` (T-M47.2):
 * `authStore.anspruchGelesen`. Gewartet wird nur, solange eine Lesung
 * AUSSTEHT (`anspruchAusstehend`) — an einer Bildschirm-Adresse wird der
 * Anspruch absichtlich nie gelesen (REQ-OPS-058 Praez. 2 (g1)/(g2)), und ein
 * Warten dort waere ein Warten bis zur Frist.
 *
 * Gibt `true` zurueck, wenn die Frist abgelaufen ist. Die Frist ist dieselbe
 * Konstante wie am Anmeldestart (REQ-OPS-052 (b)) und wird nicht zweitgefuehrt.
 */
export function warteAufAnspruch(
  store: {
    anspruchGelesen: boolean;
    anspruchAusstehend: boolean;
    $subscribe: (cb: () => void) => () => void;
  },
  fristMs: number = BOOTSTRAP_TIMEOUT_MS,
): Promise<boolean> {
  const bereit = () => store.anspruchGelesen || !store.anspruchAusstehend;
  return new Promise<boolean>((resolve) => {
    if (bereit()) { resolve(false); return; }
    let fertig = false;
    const zeitgeber = setTimeout(() => {
      if (fertig) return;
      fertig = true;
      stop();
      resolve(true);
    }, fristMs);
    const stop = store.$subscribe(() => {
      if (fertig || !bereit()) return;
      fertig = true;
      clearTimeout(zeitgeber);
      stop();
      resolve(false);
    });
  });
}

/**
 * Weist der Admin-Waechter diese Route ab (REQ-SEC-027 (c), T-M30.7)?
 *
 * Eine eigene, reine Funktion und kein Ausdruck im Waechter — damit die
 * Entscheidung ohne Navigation pruefbar ist: eine Navigation luede die
 * Zielansicht samt ihrem halben Importgraphen, und der Beleg maesse dann
 * Ladbarkeit statt Zustaendigkeit.
 *
 * **Nur der Wahrheitswert `true` sperrt** — dieselbe strenge Lesart wie beim
 * Anspruch (REQ-SEC-028 (a)), nur in der Gegenrichtung: ein Merkmal, das man
 * versehentlich als Zeichenkette setzt, sperrt NICHT und faellt damit im
 * Waechter auf, statt still eine Ansicht zu verschliessen.
 */
export function adminGateVerletzt(
  meta: { requiresAdmin?: unknown },
  istAdmin: boolean,
): boolean {
  return meta.requiresAdmin === true && !istAdmin;
}

router.beforeEach(async (to) => {
  const authStore    = useAuthStore();
  const companyStore = useCompanyStore();

  // 1. Wait for auth to resolve (guard against race condition: check before subscribing)
  if (authStore.isLoading) {
    const abgelaufen = await warteAufAnmeldestart(authStore);
    if (abgelaufen) authStore.failBootstrapTimeout('router');
  }

  // 1a. Der Start ist gescheitert (Fehler oder Fristablauf, REQ-OPS-052 (c)/(e)):
  //     die Navigation laeuft durch, ohne dass hier ueber die Anmeldung
  //     entschieden wird — sie ist ja gerade nicht entschieden. `App.vue` zeigt
  //     an dieser Stelle die Fehleransicht statt des Inhalts; eine Umleitung zur
  //     Anmeldung wuerde sie verdecken und nach einem gelungenen Neuversuch das
  //     Ziel des Nutzers verlieren. Die drei Bildschirm-Ansichten und die
  //     Anmeldeseite bleiben unberuehrt — sie brauchen den Start nicht.
  if (authStore.bootstrapError) return true;

  const requiresAuth     = to.meta.requiresAuth !== false;
  const noCompanyRequired = to.meta.noCompanyRequired === true;

  // 1b. Der Anspruch muss GELESEN sein, bevor der Waechter an ihm entscheidet
  //     (T-M47.2 Kriterium 5, REQ-OPS-058 Praez. 2 (e) samt Nachtrag
  //     2026-10-05 an Praez. 4). Bei der FRISCHEN Anmeldung ist der Start
  //     schon beendet, waehrend der Anspruch noch gelesen wird — Schritt 1
  //     wartet dort nicht. Gewartet wird nur, wo der Waechter am Anspruch
  //     entscheidet: Schritt 4 (Anmeldeseite), 4a (Admin-Flaechen) und 5
  //     (Mandant verlangt). Laeuft die Frist ab, gilt die Wahl als nicht
  //     bestaetigt — auf die Auswahl, nie in ein stehendes Ladebild
  //     (REQ-OPS-058 Praez. 2 (c)).
  const entscheidetAmAnspruch = authStore.isAuthenticated && (
    to.name === 'login'
    || to.meta.requiresAdmin === true
    || (requiresAuth && !noCompanyRequired)
  );
  if (entscheidetAmAnspruch && !authStore.anspruchGelesen) {
    const abgelaufen = await warteAufAnspruch(authStore);
    if (abgelaufen && to.name !== 'select-company') return { name: 'select-company' };
  }

  // 2. Session-Timeout prüfen (nur wenn eingeloggt und Route erfordert Auth)
  if (requiresAuth && authStore.isAuthenticated) {
    const expired = await authStore.checkSessionExpiry();
    if (expired) {
      // Session abgelaufen → zu Login umleiten
      return { name: 'login', query: { reason: 'session_expired' } };
    }
  }

  // 3. Not authenticated → Login
  if (requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // 4. Authenticated but on login → Jobs
  //
  // ⚠ NACHGEFUEHRT AM 2026-09-08 (T-M34.4, REQ-OPS-058 Praez. 2). WORTLAUT BIS
  // DAHIN: `return companyStore.hasCompany ? { name: 'jobs' } : …`. Auch diese
  // Stelle entschied aus der GELADENEN Liste und schickte den Admin nach einem
  // Neuladen auf die Auswahl. Sie fragt jetzt dasselbe Verdikt wie Schritt 5;
  // beim ersten der beiden Aufrufe faellt der Lesevorgang an, beim zweiten
  // nicht mehr. *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.*
  if (to.name === 'login' && authStore.isAuthenticated) {
    const verdikt = await companyStore.bestaetigeGespeicherteWahl();
    return verdikt === 'bestaetigt' ? { name: 'jobs' } : { name: 'select-company' };
  }

  // 4a. Die drei Admin-Flaechen (REQ-SEC-027 (c), T-M30.7 Kriterium 9).
  //     ⚠ DAS VERBERGEN IST BEQUEMLICHKEIT — DIE DURCHSETZUNG LIEGT IN DEN
  //     RULES (REQ-SEC-029, T-M30.3). Ein Waechter im Browser haelt niemanden
  //     auf, der die Abfrage von Hand stellt; er verhindert, dass ein
  //     Kundenbenutzer in eine Ansicht laeuft, die ihm nur Fehler zeigen kann.
  //     Ziel der Umleitung ist `/jobs` — die Startadresse der Anwendung; sie
  //     traegt kein `requiresAdmin` und kann deshalb keine Schleife bilden.
  if (adminGateVerletzt(to.meta, authStore.isAdmin)) {
    return { name: 'jobs' };
  }

  // 5. Authenticated but no company selected → Company selector
  //    (skip for routes that don't need a company context)
  //
  // ⚠ NACHGEFUEHRT AM 2026-09-08 (T-M34.4, REQ-OPS-058 Praez. 2 (a)/(b)).
  // WORTLAUT BIS DAHIN:
  //   if (requiresAuth && !noCompanyRequired && !companyStore.hasCompany) {
  // `hasCompany` sucht die gewaehlte Firma in der GELADENEN Liste — und die
  // kommt erst nach der Anmeldung (`App.vue`), waehrend `pf_company_id` im
  // `localStorage` ueberlebt. Der Waechter gewann dieses Rennen bei jedem
  // Neuladen und fuehrte den Admin grundlos auf die Auswahl (OP-167).
  // *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.*
  //
  // Die Zugehoerigkeit kommt jetzt aus GESPEICHERTER KENNUNG PLUS ANSPRUCH,
  // das Mandanten-Dokument aus einem gezielten `getDoc` — kein zweites Abo und
  // kein Nachladen der Liste (REQ-SEC-029 (e)). **Die Frist ist die des
  // Anmeldestarts und wird nicht zweitgefuehrt** (REQ-OPS-052 (b)/(e)): sie
  // steckt im Vorgabewert von `bestaetigeGespeicherteWahl`. Nach ihrem Ablauf
  // fuehrt der Weg auf die Auswahl, nie in ein stehendes Ladebild.
  if (requiresAuth && !noCompanyRequired) {
    const verdikt = await companyStore.bestaetigeGespeicherteWahl();
    if (verdikt !== 'bestaetigt') return { name: 'select-company' };
  }

  return true;
});

export default router;
